import { getAlgoliaRatingQuery } from 'containers/HomePage/services/api-helper';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _has from 'lodash/has';
import { getAlgoliaFilters } from 'containers/Main/utils';
import Config from 'utils/getEnvConfig';
import { ARTICLE_TYPES } from 'utils/clientSettings';
import { getArticlesMinimumWordCount } from 'containers/Resources/utils';

const {
  ALGOLIA: { SEARCH_INDEX },
} = Config;

export const addSectionToSuggestions = (
  suggestions,
  hits,
  sectionTitle,
  suggestionsType,
) => {
  if (hits.length > 0) {
    return suggestions.concat({
      title: sectionTitle,
      suggestions: hits.map(({ nameTitle, url, type, subtype }) => ({
        title: nameTitle,
        url,
        type: suggestionsType || type,
        subtype,
        blog: type === 'Blogs',
      })),
    });
  }
  return suggestions;
};

export const getQueryObject = (indexName, query, filters) => ({
  indexName,
  query,
  params: {
    hitsPerPage: 3,
    filters,
  },
});

export const getFiltersString = (clientDetails, config, locale) => {
  const prefix = locale !== 'en-US' ? `${locale}_` : '';

  const shouldFilterShortArticles = _get(
    clientDetails,
    'metadata.filterShortArticles',
    false,
  );
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const excludedAssessments = _get(
    clientDetails,
    'excludeAssessmentCollection.items',
  );
  const { algoliaAssessmentsQuery = '' } = getAlgoliaFilters({
    excludedAssessments,
  });

  const excludedTopicSlug = !_isEmpty(clientExcludedTopic)
    ? clientExcludedTopic.map(topic => topic.slug)
    : [];

  const clientQuery = !_isEmpty(_get(clientDetails, 'clientGroup'))
    ? `(clients:"none" OR clients:"${
        clientDetails.shortName
      }" OR clients:"${_get(
        clientDetails,
        'clientGroup.shortName',
      )}") AND NOT clientsExclude:"${
        clientDetails.shortName
      }" AND NOT clientsExclude:"${_get(
        clientDetails,
        'clientGroup.shortName',
      )}"`
    : `(clients:"none" OR clients:"${clientDetails.shortName}") AND NOT clientsExclude:"${clientDetails.shortName}"`;
  let filtersString = clientQuery;

  if (!_isEmpty(excludedTopicSlug)) {
    _forEach(excludedTopicSlug, slug => {
      filtersString += ` AND NOT slug:'${slug}' AND NOT filterTopics:'${slug}'`;
    });
  }

  if (algoliaAssessmentsQuery) {
    filtersString += algoliaAssessmentsQuery;
  }

  if (
    !_isEmpty(_get(clientDetails, 'metadata.hideCostDescriptionResources', []))
  ) {
    filtersString += ` AND ${_get(
      clientDetails,
      'metadata.hideCostDescriptionResources',
      [],
    )
      .map(item => `NOT ${prefix}costDescription:'${item}'`)
      .join(' AND ')}`;
  }

  const averageRatingCutoff = _get(clientDetails, 'averageRatingCutoff');
  const expertRatingCutoff = _get(clientDetails, 'expertRatingCutoff');
  const userRatingCutoff = _get(clientDetails, 'userRatingCutoff');

  const ratingFilter = getAlgoliaRatingQuery({
    averageRatingCutoff,
    expertRatingCutoff,
    userRatingCutoff,
  });
  if (ratingFilter) {
    filtersString += ratingFilter;
  }

  if (shouldFilterShortArticles) {
    const articlesMinimumWordCount = getArticlesMinimumWordCount(
      config,
      clientDetails,
    );
    filtersString += ` AND wordCount > ${articlesMinimumWordCount}`;
  }

  return filtersString;
};

const SEARCH_FIELDS = [
  'nameTitle',
  'relatedTopics',
  'type',
  'aliases',
  'keywords',
  'personOrganization.name',
];

const SERIES_SEARCH_FIELDS = ['nameTitle', 'description'];

export const buildSearchAlgoliaQueries = ({
  filters,
  optionalFilters,
  minProximity,
  query,
  excludedTopics,
  excludeResourceTypes,
  audienceFilters,
  excludedTagsFilters,
  stateFilters,
  alwaysShowInformationalLink,
}) => {
  const restrictSearchableAttributes = SEARCH_FIELDS;
  const seriesRestrictSearchableAttributes = SERIES_SEARCH_FIELDS;
  const excludeResourceTypesFilters = _isEmpty(excludeResourceTypes)
    ? ''
    : ` AND ${excludeResourceTypes
        .map(type => {
          if (type === 'Articles' && alwaysShowInformationalLink) {
            return `(NOT type:"${type}" OR articlesSubtype:"${ARTICLE_TYPES.INFORMATION_LINK}")`;
          }
          return `NOT type:"${type}"`;
        })
        .join(' AND ')}`;
  const excludedTopicsFilters = _isEmpty(excludedTopics)
    ? ''
    : ` AND ${excludedTopics
        .map(topic => `NOT objectID:'${topic}'`)
        .join(' AND ')}`;
  const excludedTopicsResourcesFilters = _isEmpty(excludedTopics)
    ? ''
    : ` AND ${excludedTopics
        .map(topic => `NOT relatedTopics.id:'${topic}'`)
        .join(' AND ')}`;

  const baseQueries = [
    {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 2,
        filters: `${filters} AND type:'Topics'${excludedTopicsFilters}`,
        optionalFilters,
        getRankingInfo: true,
        minProximity,
        restrictSearchableAttributes,
        advancedSyntax: true,
        queryType: 'prefixLast',
      },
    },
    {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 1,
        filters: `${filters} AND type:'Assessments'${audienceFilters}${excludedTopicsResourcesFilters}`,
        optionalFilters,
        getRankingInfo: true,
        exactOnSingleWordQuery: 'word',
        advancedSyntax: true,
        minProximity,
        restrictSearchableAttributes,
        queryType: 'prefixLast',
      },
    },
    {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 3,
        filters: `${filters} AND type:'Insights'${audienceFilters}${excludedTopicsResourcesFilters}`,
        optionalFilters,
        getRankingInfo: true,
        minProximity,
        restrictSearchableAttributes,
        queryType: 'prefixLast',
      },
    },
    {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 18,
        filters: `${filters} AND NOT type:'Topics' AND NOT type:'Assessments' AND NOT type:'Insights' AND NOT type:'Series'${excludeResourceTypesFilters} AND NOT displayOnly:true${stateFilters}${excludedTagsFilters}${audienceFilters}${excludedTopicsResourcesFilters}`,
        optionalFilters,
        getRankingInfo: true,
        minProximity,
        restrictSearchableAttributes,
        queryType: 'prefixLast',
      },
    },
    !excludeResourceTypes.includes('Series') && {
      indexName: SEARCH_INDEX,
      query,
      params: {
        hitsPerPage: 3,
        filters: "type:'Series'",
        optionalFilters,
        getRankingInfo: true,
        minProximity,
        restrictSearchableAttributes: seriesRestrictSearchableAttributes,
        queryType: 'prefixLast',
      },
    },
  ].filter(Boolean);

  const exactQueries = baseQueries.map(q => ({
    ...q,
    params: {
      ...q.params,
      typoTolerance: false,
    },
  }));

  let dynamicTypoParams = {};
  const queryLength = query.length;

  if (queryLength < 6) {
    dynamicTypoParams = {
      typoTolerance: true,
      minWordSizefor1Typo: 5,
    };
  } else {
    dynamicTypoParams = {
      typoTolerance: true,
      minWordSizefor1Typo: 3,
      minWordSizefor2Typos: 6,
    };
  }

  const typoQueries = baseQueries.map(q => ({
    ...q,
    params: {
      ...q.params,
      ...dynamicTypoParams,
    },
  }));

  return [...exactQueries, ...typoQueries];
};

export const getExclusiveBadge = (resource, clientDetails) => {
  const dataClients = _get(resource, 'clients', []).map(el =>
    el?.toLowerCase(),
  );
  const resourceType = _get(resource, 'type');

  const isClientExclusive = dataClients.includes(
    _get(clientDetails, 'shortName')?.toLowerCase(),
  );
  const isClientGroupExclusive = dataClients.includes(
    _get(clientDetails, 'clientGroup.shortName')?.toLowerCase(),
  );

  const showClientBadge =
    isClientExclusive ||
    isClientGroupExclusive ||
    resourceType === 'Client Exclusive';

  const hasCustomBadge =
    _has(clientDetails, 'metadata.clientExclusiveIcon') && isClientExclusive;
  const hasAssetLogo =
    _has(clientDetails, 'logosCollection.items[0].url') && isClientExclusive;
  const isReverseLogoOrder = _get(clientDetails, 'metadata.isReverseLogoOrder');

  const badge = hasCustomBadge
    ? _get(clientDetails, 'metadata.clientExclusiveIcon')
    : hasAssetLogo
    ? _get(clientDetails, 'logosCollection.items[0].url')
    : isReverseLogoOrder
    ? _get(clientDetails, 'crediblemindLogo.url')
    : _get(clientDetails, 'logoReverse.url');

  const badgeTitle = isClientExclusive
    ? _get(clientDetails, 'metadata.clientExclusiveResource.tooltipBadge', '')
    : _get(
        clientDetails,
        'clientGroup.metadata.clientExclusiveResource.tooltipBadge',
        '',
      );

  return {
    hasCustomBadge,
    showClientBadge,
    badge,
    badgeTitle,
  };
};

export const addInternalTyping = (resource, internalType) => ({
  ...resource,
  internalType,
});

export const sortSearchPreviewResources = (resources, previewOrder) => {
  if (_isEmpty(previewOrder)) return resources;
  const groupResources = resources.reduce((result, item) => {
    const { internalType, ...rest } = item;
    const updatedResult = { ...result };

    if (!updatedResult[internalType])
      updatedResult[internalType] = {
        type: internalType,
        order: previewOrder[internalType] || 0,
        content: [],
      };

    updatedResult[internalType].content.push(rest);

    return updatedResult;
  }, {});

  const resourcesTypes = Object.keys(groupResources);

  Object.entries(previewOrder)
    .sort(([, orderA], [, orderB]) => orderB - orderA)
    .forEach(([itemToMove]) => {
      let indexToMove = previewOrder[itemToMove] - 1;

      if (indexToMove !== undefined) {
        if (indexToMove >= resourcesTypes.length || indexToMove < 0) {
          indexToMove = resourcesTypes.length - 1;
        }

        const itemIndex = resourcesTypes.indexOf(itemToMove);
        if (itemIndex !== -1) {
          const [removedItem] = resourcesTypes.splice(itemIndex, 1);
          resourcesTypes.splice(indexToMove, 0, removedItem);
        }
      }
    });

  const sortedResources = resourcesTypes.flatMap(
    type => groupResources[type].content,
  );

  return sortedResources;
};

export function pickTopResources(
  resources,
  finalQuery,
  clientData,
  resourcePriority,
) {
  // eslint-disable-next-line no-underscore-dangle
  const getRankMetric = (obj, k, def = 0) => obj?._rankingInfo?.[k] ?? def;

  const compareRelevance = (a, b) => {
    if (getRankMetric(a, 'nbExactWords') !== getRankMetric(b, 'nbExactWords'))
      return (
        getRankMetric(b, 'nbExactWords') - getRankMetric(a, 'nbExactWords')
      );

    if (getRankMetric(a, 'nbTypos') !== getRankMetric(b, 'nbTypos'))
      return getRankMetric(a, 'nbTypos') - getRankMetric(b, 'nbTypos');

    if (getRankMetric(a, 'words') !== getRankMetric(b, 'words'))
      return getRankMetric(b, 'words') - getRankMetric(a, 'words');

    if (
      getRankMetric(a, 'proximityDistance') !==
      getRankMetric(b, 'proximityDistance')
    )
      return (
        getRankMetric(a, 'proximityDistance') -
        getRankMetric(b, 'proximityDistance')
      );

    return getRankMetric(b, 'userScore') - getRankMetric(a, 'userScore');
  };

  const getPriority = type => resourcePriority?.[type] ?? 99;

  const byPriorityThenScore = (a, b) => {
    const aPriority = getPriority(a.type);
    const bPriority = getPriority(b.type);
    if (aPriority !== bPriority) return aPriority - bPriority;
    return compareRelevance(a, b);
  };

  const sorted = resources.sort(byPriorityThenScore);

  const firstThree = [];
  const seenTypes = [];

  sorted.forEach(res => {
    if (firstThree.length < 3 && !seenTypes.includes(res.type)) {
      firstThree.push(res);
      seenTypes.push(res.type);
    }
  });

  return firstThree.length === 3
    ? firstThree
    : [...firstThree, ...sorted.filter(hit => !firstThree.includes(hit))].slice(
        0,
        3,
      );
}
